import Joi from 'joi';

import trainTime from './lib/TrainTime';
import { translate as t } from './lib/I18n';
import { Brands, Validation } from './constants/AppConstants';
import config from './config';
import _ from 'lodash';

const validators = {

    required (requiredMessage = 'alertmessage.required', emptyMessage = 'alertmessage.empty') {
        return Joi.any().invalid(null, '').required().options({
            language: {
                any: {
                    invalid: t(requiredMessage),
                    required: t(requiredMessage),
                    empty: t(emptyMessage)
                }
            }
        });
    },

    optional () {
        return Joi.any().optional();
    },

    name (min = 1, max = 19) {
        return Joi.string().regex(Validation.lettersAndSpaces).trim().required().min(min).max(max).options({
            language: {
                any: {
                    required: t('alertmessage.required'),
                    empty: t('alertmessage.empty')
                },
                string: {
                    base: t('alertmessage.string.min', { num: min }),
                    min: t('alertmessage.string.min', { num: min }),
                    max: t('alertmessage.string.max', { num: max }),
                    regex: {
                        base: t('alertmessage.badName')
                    }
                }
            }
        });
    },

    requiredString (min = 1, max, msg = 'alertmessage.required') {
        let validator = Joi.string().trim().required().min(min);
        let options = {
            language: {
                any: {
                    required: t(msg),
                    empty: t('alertmessage.empty')
                },
                string: {
                    base: t(msg),
                    min: min === 1 ? t(msg) : t('alertmessage.string.min', { num: min })
                }
            }
        };

        if (_.isNumber(max)) {
            validator = validator.max(max);
            options.language.string.max = t('alertmessage.string.max', { num: max });
        }

        return validator.options(options);
    },

    voucherAmount (maxAmount) {
        return Joi.number().min(0).max(maxAmount).required().options({
            language: {
                number: {
                    base: t('voucher.error.number.base'),
                    min: t('voucher.error.number.min'),
                    max: t('voucher.error.number.max', { maxAmount: maxAmount.toFixed(2) })
                }
            }
        });
    },

    price () {
        return Joi.number().min(0).required().options({
            language: {
                number: {
                    base: t('alertmessage.price'),
                }
            }
        });
    },

    cardHolderName () {
        return Joi.string().regex(Validation.lettersAndSpaces).regex(Validation.cardHolderName).trim().min(3).required().options({
            language: {
                any: {
                    required: t('alertmessage.badCardHolder'),
                    empty: t('alertmessage.badCardHolder')
                },
                string: {
                    base: t('alertmessage.badCardHolder'),
                    min: t('alertmessage.badCardHolder'),
                    regex: {
                        base: t('alertmessage.badCardHolder')
                    }
                }
            }
        });
    },

    photocardHolder (message = t('alertmessage.badPhotocardFirstName')) {
        return Joi.string().regex(Validation.photocardHolder).trim().required().options({
            language: {
                any: {
                    required: message,
                    empty: message
                },
                string: {
                    base: message,
                    min: message,
                    regex: {
                        base: message
                    }
                }
            }
        });
    },

    cardNumber () {
        return Joi.string().trim().creditCard().required().options({
            language: {
                string: {
                    base: t('alertmessage.badCardNumber'),
                    creditCard: t('alertmessage.badCardNumber')
                }
            }
        });
    },

    isrn () {
        return Joi.string().trim().regex(Validation.keySmartcardNumber).required().options({
            language: {
                any: {
                    empty: t('alertmessage.smartcard', { fieldName: t('account.smartcard.text.label') })
                },
                string: {
                    base: t('alertmessage.smartcard', { fieldName: t('account.smartcard.text.label') }),
                    regex: {
                        base: t('alertmessage.smartcard', { fieldName: t('account.smartcard.text.label') })
                    }
                }
            }
        });
    },

    expiryMonth () {
        return Joi.number().min(1).max(12).required().options({
            language: {
                number: {
                    base: t('alertmessage.badExpiryMonth'),
                    min: t('alertmessage.badExpiryMonth'),
                    max: t('alertmessage.badExpiryMonth')
                }
            }
        });
    },

    expiryYear () {
        return Joi.number().min(trainTime().year()).required().options({
            language: {
                number: {
                    base: t('alertmessage.badExpiryYear'),
                    min: t('alertmessage.badExpiryYear'),
                    max: t('alertmessage.badExpiryYear')
                }
            }
        });
    },

    isAdult () {
        return Joi.boolean().required().options({
            language: {
                boolean: {
                    base: 'test',
                }
            }
        });
    },

    cv2 () {
        return Joi.string().trim().regex(Validation.cv2).required().options({
            language: {
                any: {
                    empty: t('alertmessage.badCvv')
                },
                string: {
                    base: t('alertmessage.badCvv'),
                    regex: {
                        base: t('alertmessage.badCvv')
                    }
                }
            }
        });
    },

    addressLine () {
        return Joi.string().trim().min(1).max(50).options({
            language: {
                any: {
                    empty: t('alertmessage.badAddressLine')
                },
                string: {
                    base: t('alertmessage.badAddressLine'),
                    min: t('alertmessage.badAddressLine'),
                    max: t('alertmessage.string.max', { num: 50 })
                }
            }
        });
    },

    textMaxLength (maxLength = 50) {
        return Joi.string().trim().max(maxLength).allow([ null, '' ]).options({
            language: {
                string: {
                    base: t('alertmessage.string.max', { num: maxLength }),
                    max: t('alertmessage.string.max', { num: maxLength })
                }
            }
        });
    },

    townCity () {
        return Joi.string().trim().min(2).max(50).required().options({
            language: {
                any: {
                    empty: t('alertmessage.badTownCity')
                },
                string: {
                    base: t('alertmessage.badTownCity'),
                    min: t('alertmessage.badTownCity'),
                    max: t('alertmessage.string.max', { num: 50 })
                }
            }
        });
    },

    postcode () {
        return Joi.string().trim().regex(Validation.postcode).required().options({
            language: {
                any: {
                    empty: t('alertmessage.badPostCode')
                },
                string: {
                    base: t('alertmessage.badPostCode'),
                    regex: {
                        base: t('alertmessage.badPostCode')
                    }
                }
            }
        });
    },

    country () {
        return Joi.string().trim().regex(Validation.countryCode).required().options({
            language: {
                any: {
                    empty: t('alertmessage.badCountry')
                },
                string: {
                    base: t('alertmessage.badCountry'),
                    regex: {
                        base: t('alertmessage.badCountry')
                    }
                }
            }
        });
    },

    email () {
        return Joi.string().trim().email({ minDomainAtoms: 2 }).required().options({
            language: {
                any: {
                    empty: t('alertmessage.badEmail')
                },
                string: {
                    base: t('alertmessage.badEmail'),
                    email: t('alertmessage.badEmail')
                }
            }
        });
    },

    confirmEmail () {
        const brand = config.get('brand');
        const isGTR = _.values(Brands).includes(brand);

        const baseValidator = Joi.string().trim().valid(Joi.ref('email')).options({
            language: {
                any: {
                    allowOnly: t('alertmessage.fieldsNoMatch', { field: 'Email' }),
                    empty: t('alertmessage.required')
                },
                string: {
                    base: t('alertmessage.required')
                }
            }
        });

        if (isGTR) {
            return baseValidator.required();
        } else {
            return baseValidator.optional();
        }
    },

    phone (maxLength = 20) {
        return Joi.string().trim().regex(Validation.telephone).max(maxLength).options({
            language: {
                any: {
                    empty: t('alertmessage.badContactNumber')
                },
                string: {
                    base: t('alertmessage.badContactNumber'),
                    max: t('alertmessage.string.max', { num: maxLength }),
                    regex: {
                        base: t('alertmessage.badContactNumber')
                    }
                }
            }
        });
    },

    passwordEntry () {
        return this.requiredString().options({
            language: {
                string: {
                    base: t('alertmessage.login.badPassword'),
                    min: t('alertmessage.login.badPassword')
                }
            }
        });
    },

    passwordCreate () {
        return Joi.string()
            .trim()
            .min(config.get('minPasswordLength'))
            .regex(/[a-z]/, 'lowercase characters')
            .regex(/[A-Z]/, 'uppercase characters')
            .regex(/\d/, 'digits')
            .options({
                language: {
                    any: {
                        empty: t('alertmessage.badPassword'),
                    },
                    string: {
                        base: t('alertmessage.badPassword'),
                        min: t('alertmessage.badPassword.tooShort'),
                        regex: {
                            base: t('alertmessage.badPassword'),
                            // cannot put the following into the dictionary
                            // the installed version of intl-messageformat (2.x) does not support escaping
                            // it is supported in 8.x
                            name: 'One or more {{name}} required',
                        }
                    },
                },
            });
    },

    title (message = t('alertmessage.choose')) {
        return Joi.any().valid(config.get('customerTitles')).options({
            language: {
                any: {
                    allowOnly: message,
                }
            }
        });
    },

    regex (regex, fieldName = t('alertmessage.regex.fieldName'), alertmessage = 'alertmessage.regex') {
        return Joi.string().regex(regex).options({
            language: {
                any: {
                    required: t('alertmessage.required')
                },
                string: {
                    base: t(alertmessage, { fieldName }),
                    regex: {
                        base: t(alertmessage, { fieldName })
                    }
                }
            }
        });
    },

    allowOnly (list) {
        return Joi.string().only(list).options({
            language: {
                any: {
                    allowOnly: t('alertmessage.choose'),
                    required: t('alertmessage.required'),
                    empty: t('alertmessage.required')
                },
                string: {
                    base: t('alertmessage.required')
                }
            }
        });
    },

    true: () => validators.boolean(true, 'alertmessage.required'),

    boolean (value = false, message = 'alertmessage.required') {
        return Joi.boolean().only([ !!value ]).options({
            language: {
                any: {
                    allowOnly: t(message),
                    required: t(message),
                    empty: t(message)
                },
                boolean: {
                    base: t(message)
                }
            }
        });
    },

    isOver16: () => {
        return Joi.boolean().only(true).options({
            language: {
                any: {
                    allowOnly: t('alertmessage.date.over16'),
                    required: t('alertmessage.date.over16'),
                    empty: t('alertmessage.date.over16'),
                },
                boolean: {
                    base: t('alertmessage.date.over16'),
                }
            }
        });
    },

    sortCode: () => {
        return validators.regex(Validation.sortCode).required().options({
            language: {
                any: {
                    empty: t('alertmessage.badSortCode')
                },
                string: {
                    base: t('alertmessage.badSortCode'),
                    regex: {
                        base: t('alertmessage.badSortCode')
                    }
                }
            }
        });
    },

    accountNumber: () => {
        return validators.regex(Validation.accountNumber).required().options({
            language: {
                any: {
                    empty: t('alertmessage.badAccountNumber')
                },
                string: {
                    base: t('alertmessage.badAccountNumber'),
                    regex: {
                        base: t('alertmessage.badAccountNumber')
                    }
                }
            }
        });
    },

    railcardsNumber (passengersNumberRef) {
        return Joi.number().min(0).max(Joi.ref(passengersNumberRef)).options({
            language: {
                number: {
                    max: t('alertmessage.form.moreRailcardsThanPassengers')
                }
            }
        });
    },

    collectionLocation (required = false) {
        return required ? Joi.any().invalid(null, '').required().options({
            language: {
                any: {
                    invalid: t('alertmessage.required'),
                    required: t('alertmessage.required'),
                    empty: t('alertmessage.empty')
                }
            }
        }) : Joi.any().optional();
    }
};


export default validators;



// WEBPACK FOOTER //
// ./src/validation.js